import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EdlCommonModalService, FileDownloadHandler, SessionService, User } from '@edl-development/edl-common';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { EdlComment } from 'src/app/model/comment.model';
import { CommentState } from 'src/app/model/enum/comment-state.enum';
import { ObservationTypes } from 'src/app/model/enum/observation-types.enum';
import { PermissionTypes } from 'src/app/model/enum/permission-types.enum';
import { FileAttachment } from 'src/app/model/attachment/file-attachment.model';
import { ObservationSummaryDTO } from 'src/app/model/observation/observation-summary-dto.model';
import { CommentService } from 'src/app/services/comment.service';
import { FileService } from 'src/app/services/file.service';
import { ObservationCommentService } from '../../services/observation-comment/observation-comment.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ModalService } from 'src/app/modals/modal.service';

const DEFAULT_HISTORICAL_LABEL = 'Previous';

@Component({
    selector: 'app-observation-comment-cell',
    templateUrl: './observation-comment-cell.component.html',
    styleUrls: ['./observation-comment-cell.component.scss']
})
export class ObservationCommentCellComponent implements OnInit {
    @Input() set comment(value: EdlComment | undefined) {
        this._comment = value;

        this.sanitizedCommentText = value?.comment ?
            this.sanitized.bypassSecurityTrustHtml(value?.comment) :
            value?.comment;
    }

    get comment() {
        return this._comment;
    }

    @Input() observationType: any;
    @Input() observationSummary?: ObservationSummaryDTO;
    @Input() hideFilter: boolean = false;
    @Input() evalUser?: User | null;
    @Input() defaultLookupValue?: string;
    @Input() isEditMode: boolean = false;
    @Input() isReadOnly: boolean = false;
    @Input() attachmentLabel: string = "";
    @Output() commentChange = new EventEmitter<EdlComment>();
    @Output() isEditModeChange = new EventEmitter<boolean>();

    private _comment: EdlComment | undefined;
    sanitizedCommentText: SafeHtml | undefined;

    CommentState = CommentState;
    isMyComment: boolean = false;
    historicLabel: string = DEFAULT_HISTORICAL_LABEL;
    PermissionTypes = PermissionTypes;

    constructor(
        private edlCommonModalService: EdlCommonModalService,
        private observationCommentService: ObservationCommentService,
        private commentService: CommentService,
        private sessionService: SessionService,
        private toastr: ToastrService,
        private fileService: FileService,
        private fileHandler: FileDownloadHandler,
        private sanitized: DomSanitizer
    ) { }

    ngOnInit(): void {
        this.isMyComment = this.sessionService.user?.UserID === this.comment?.userId;

        const commentObservationType = ObservationTypes.GetObservationType(this.comment?.evalType);
        this.historicLabel = (this.observationType?.Type !== this.comment?.evalType && commentObservationType?.Name) ?
            this.overrideHistoricLabel(commentObservationType) :
            DEFAULT_HISTORICAL_LABEL;
    }

    addComment() {
        this.setIsEditMode(true);
    }

    openComment(comment: EdlComment) {
        if (comment.userId == this.evalUser?.UserID) {
            this.setIsEditMode(true);
        }
    }

    private setIsEditMode(value: boolean) {
        this.isEditMode = value;
        this.isEditModeChange.emit(this.isEditMode);
    }

    private overrideHistoricLabel(commentObservationType: any) {
        if (commentObservationType.Type == ObservationTypes.Formal.Type) {
            return "Evidence";
        }
        return commentObservationType?.Name
    }

    deleteComment(comment: EdlComment) {
        this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to delete this comment?')
            .then(() => {
                firstValueFrom(
                    this.commentService
                        .deleteComment(comment)
                ).then(() => {
                    this.refreshCommentList();
                    this.observationCommentService.onAttachmentChange(comment);
                    this.toastr.success("Comment deleted.")
                }).catch(error => {
                    this.toastr.error(error);
                });
            });
    }

    publishComment(comment: EdlComment) {
        if (comment.commentState == CommentState.DRAFT) comment.commentState = CommentState.PUBLISHED; //this depends on the state of the observatio, no? Might need to move this into the backend
        this.observationCommentService.saveComment(comment)
            .then((result: EdlComment | null | undefined) => {
                if (result) {
                    this.setComment(result);
                    this.refreshCommentList();
                    this.toastr.success("Comment published successfully.");
                } else {
                    this.toastr.error('Comment failed to be published.');
                }
            }).catch(error => {
                this.toastr.error(error);
            });
    }

    commentState(comment: EdlComment): string {
        switch (comment.commentState) {
            case CommentState.PUBLISHED: return "Published";
            default: return "Draft";
        }
    }

    getCommentClass(comment: EdlComment): string {
        return this.commentState(comment).replace(/\s/g, "").replace(/\//g, "").toLocaleLowerCase();
    }

    refreshCommentList() {
        this.observationCommentService.reloadComments();
    }

    onClose() {
        this.setIsEditMode(false);
        this.refreshCommentList();
    }

    setComment(modifiedComment: EdlComment) {
        this.comment = modifiedComment;
        this.commentChange.emit(this.comment);
    }

    viewAttachement(attachment: FileAttachment) {
        this.fileService
            .getFileResponse(attachment.fileID)
            .subscribe((fileResponse: Blob) => {
                this.fileHandler.saveFile(fileResponse, attachment.fileName);
            });

    }

    deleteAttachement(attachment: FileAttachment) {
      this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to delete this attachment?')
      .then(() => {
        this.fileService
            .deleteFile(attachment)
            .subscribe((resp: any) => {
                this.toastr.success("Attachment removed.");

                const index = this.comment?.attachments.indexOf(attachment, 0);
                if (index! > -1) {
                    this.comment?.attachments.splice(index!, 1);
                }

                this.observationCommentService.onAttachmentChange(this.comment!);
            });


      });
    }
}
